// V10 — Firefly replaces the Activity pill as the dock collapsed affordance.
// F8/F9: calm blinking, idle roam, smooth drag-chase.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, PointerEvent as ReactPointerEvent } from "react";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import type { KpiTone } from "@/components/common/kpi/kpiTypes";
import { isHorizontalEdge, type DockEdge } from "./activityDockPositionMath";
import {
  dockAffordanceDragStyle,
  dockAffordanceRestingStyle,
} from "./activityDockAffordancePosition";
import { ActivityDockGoldenHaloRings } from "./ActivityDockGoldenHaloRings";
import { formatImpersonationAriaSuffix } from "./impersonationBanner";
import { useActivityDockGoldenHalo } from "./useActivityDockGoldenHalo";
import { Firefly } from "./firefly/Firefly";
import { FireflyViewportPortal } from "./firefly/FireflyViewportPortal";
import { useActivityDockFireflyHighlight } from "./firefly/useActivityDockFireflyHighlight";
import {
  FIREFLY_HALO_DIAMETER_PX,
  fireflyCircleTabLayout,
} from "./firefly/fireflyDockGeometry";
import { useFireflyDragChase } from "./firefly/useFireflyDragChase";
import { fireflyAnchorScreenFromTabRect } from "./firefly/fireflyChaseMotion";
import { useFireflyIdleRoaming } from "./firefly/useFireflyIdleRoaming";
import { useFireflyTabAnchorScreen } from "./firefly/useFireflyTabAnchorScreen";
import { useFireflyDockMotionRecover } from "./firefly/useFireflyDockMotionRecover";
import type { FireflyTourDockOverride } from "./firefly/useFireflyTourSession";
import { useWorkplaceStreamChatSession } from "@/contexts/WorkplaceStreamChatSessionContext";
import { openFireflyActivityDock } from "@/components/activity/activityDockOpenEvent";

export type ActivityDockFireflyTabProps = {
  edge: DockEdge;
  offsetRatio: number;
  dragPreview: { x: number; y: number } | null;
  tabWidthPx: number;
  tabHeightPx: number;
  tone: KpiTone | null;
  summary: string;
  ariaLabel: string;
  /** Hover tooltip explaining non-ordinary Firefly state (when set). */
  statusReasonTitle?: string | null;
  expanded: boolean;
  isDragging: boolean;
  blinkingSuppressed: boolean;
  impersonating?: boolean;
  impersonatingRoles?: readonly string[];
  onPointerDown: (e: ReactPointerEvent<HTMLElement>) => void;
  onPointerMove: (e: ReactPointerEvent<HTMLElement>) => void;
  onPointerUp: (e: ReactPointerEvent<HTMLElement>) => void;
  onPointerCancel: (e: ReactPointerEvent<HTMLElement>) => void;
  /** TOUR-4a — hide dock bug + lock intent while guided tour owns the firefly. */
  tourDockOverride?: FireflyTourDockOverride | null;
  /** PTT-4 — tab long-press listening ring on collapsed firefly. */
  pttListening?: boolean;
};

export function ActivityDockFireflyTab({
  edge,
  offsetRatio,
  dragPreview,
  tabWidthPx,
  tabHeightPx,
  tone,
  summary,
  ariaLabel,
  statusReasonTitle = null,
  expanded,
  isDragging,
  blinkingSuppressed,
  impersonating = false,
  impersonatingRoles = [],
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
  tourDockOverride = null,
  pttListening = false,
}: ActivityDockFireflyTabProps) {
  const tabRef = useRef<HTMLButtonElement>(null);
  const tourActive = tourDockOverride?.active ?? false;
  const { unreadCount: messengerUnread } = useWorkplaceStreamChatSession();
  const messengerAlert = messengerUnread > 0;

  const style: CSSProperties = dragPreview
    ? dockAffordanceDragStyle(dragPreview, tabWidthPx, tabHeightPx)
    : dockAffordanceRestingStyle(edge, offsetRatio, tabWidthPx, tabHeightPx);

  const circleLayout = useMemo(
    () => fireflyCircleTabLayout(edge),
    [edge],
  );

  const anchor = useMemo(
    () => ({ x: circleLayout.centerX, y: circleLayout.centerY }),
    [circleLayout.centerX, circleLayout.centerY],
  );

  const [motionResetGeneration, setMotionResetGeneration] = useState(0);

  const anchorScreen = useFireflyTabAnchorScreen(
    tabRef,
    anchor.x,
    anchor.y,
    [edge, offsetRatio, dragPreview, tabWidthPx, tabHeightPx],
  );

  const { showHalo, playSessionHaloOnce } = useActivityDockGoldenHalo();

  useEffect(() => {
    if (expanded || tourActive) return;
    playSessionHaloOnce();
    // First mount per session only — storage gate lives in the hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getTabAnchorScreen = useCallback(() => {
    const el = tabRef.current;
    if (!el) return null;
    return fireflyAnchorScreenFromTabRect(
      el.getBoundingClientRect(),
      anchor.x,
      anchor.y,
    );
  }, [anchor.x, anchor.y]);

  const dragChase = useFireflyDragChase({
    isDragging,
    getTabAnchorScreen,
  });
  const { active: dragChaseActive, forceSettleAtAnchor } = dragChase;

  const { roaming, viewportX, viewportY } = useFireflyIdleRoaming({
    enabled: !expanded && !isDragging && !dragChaseActive && !tourActive,
    anchorScreenX: anchorScreen.x,
    anchorScreenY: anchorScreen.y,
  });

  const portaled = !tourActive && (roaming || dragChaseActive);

  const {
    intent,
    highlightGen,
    highlightPoint,
    onAnchorDwellReadyChange,
    suppressHighlightMotion,
  } = useActivityDockFireflyHighlight({
    tone,
    edge,
    tabWidthPx,
    tabHeightPx,
    pauseMotion:
      expanded ||
      isDragging ||
      blinkingSuppressed ||
      dragChaseActive ||
      tourActive ||
      roaming,
  });

  const handleDockMotionRecover = useCallback(() => {
    suppressHighlightMotion();
    forceSettleAtAnchor();
    setMotionResetGeneration((g) => g + 1);
  }, [forceSettleAtAnchor, suppressHighlightMotion]);

  const portaledFireflyX = dragChaseActive ? dragChase.viewportX : viewportX;
  const portaledFireflyY = dragChaseActive ? dragChase.viewportY : viewportY;

  useFireflyDockMotionRecover({
    enabled: !expanded,
    tourActive,
    anchorScreenX: anchorScreen.x,
    anchorScreenY: anchorScreen.y,
    portaledFireflyX,
    portaledFireflyY,
    portaled,
    onRecover: handleDockMotionRecover,
  });

  const hideTabFirefly = tourActive;

  const impersonationOutlineCls = impersonating
    ? "outline outline-2 outline-offset-2 outline-amber-500 dark:outline-amber-400"
    : "";
  // WC-V20-HOST: teal messenger unread — distinct from amber impersonation + travel halo.
  const messengerOutlineCls =
    !impersonating && messengerAlert
      ? "outline outline-2 outline-offset-2 outline-teal-500 dark:outline-teal-400"
      : "";
  const augmentedAriaLabel =
    ariaLabel +
    formatImpersonationAriaSuffix(impersonating, impersonatingRoles) +
    (messengerAlert ? ` — ${messengerUnread} unread message${messengerUnread === 1 ? "" : "s"}` : "");

  const horizontal = isHorizontalEdge(edge);

  const handlePointerUp = useCallback(
    (e: ReactPointerEvent<HTMLButtonElement>) => {
      const target = e.target;
      if (
        messengerAlert &&
        target instanceof Element &&
        target.closest("[data-messenger-badge]")
      ) {
        void openFireflyActivityDock({ force: true, focusSection: "messages" });
        return;
      }
      onPointerUp(e);
    },
    [messengerAlert, onPointerUp],
  );

  const handlePointerDown = useCallback(
    (e: ReactPointerEvent<HTMLButtonElement>) => {
      const target = e.target;
      if (
        messengerAlert &&
        target instanceof Element &&
        target.closest("[data-messenger-badge]")
      ) {
        // Don't start a dock drag from the unread badge.
        e.stopPropagation();
        return;
      }
      onPointerDown(e);
    },
    [messengerAlert, onPointerDown],
  );

  const fireflyCommon = {
    intent,
    blinkingSuppressed: blinkingSuppressed || roaming,
    onAnchorDwellReadyChange,
  };

  return (
    <>
      <button
        ref={tabRef}
        type="button"
        data-activity-dock-tab="1"
        {...{ [FIREFLY_TARGET_ATTR]: "activity-dock-tab" }}
        aria-label={augmentedAriaLabel}
        aria-expanded={expanded}
        title={statusReasonTitle || undefined}
        onPointerDown={handlePointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={onPointerCancel}
        style={style}
        className={[
          "z-[9100] relative overflow-visible rounded-full",
          "bg-transparent border-0 shadow-none",
          dragChaseActive
            ? ""
            : "transition-[left,right,top,bottom] duration-150 ease-out",
          "select-none touch-none",
          pttListening
            ? "ring-2 ring-emerald-400 ring-offset-2 ring-offset-transparent"
            : "",
          impersonationOutlineCls,
          messengerOutlineCls,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {!hideTabFirefly && !portaled ? (
          <Firefly
            {...fireflyCommon}
            anchorX={anchor.x}
            anchorY={anchor.y}
            highlightGeneration={highlightGen}
            highlightX={highlightPoint.x}
            highlightY={highlightPoint.y}
            motionResetGeneration={motionResetGeneration}
          />
        ) : null}
        {showHalo ? (
          <ActivityDockGoldenHaloRings
            centerX={circleLayout.centerX}
            centerY={circleLayout.centerY}
            diameterPx={FIREFLY_HALO_DIAMETER_PX}
          />
        ) : null}
        {messengerAlert ? (
          <span
            data-messenger-badge="1"
            className={[
              // Keep hit target small so edge / half-Firefly taps open Ask, not Messages.
              "pointer-events-auto absolute z-[2] flex h-4 w-4 items-center justify-center",
              "rounded-full bg-teal-600 text-[9px] leading-none shadow-sm",
              horizontal ? "left-0.5 top-0.5" : "left-0 top-0.5",
            ].join(" ")}
            aria-hidden
          >
            💬
          </span>
        ) : null}
        {summary ? (
          <span
            className={[
              "pointer-events-none absolute z-[1] rounded bg-black/55 px-1 py-0.5",
              "text-[9px] font-semibold tabular-nums text-white",
              horizontal ? "right-1 top-1" : "right-0.5 top-1",
            ].join(" ")}
            aria-hidden
          >
            {summary}
          </span>
        ) : null}
      </button>
      {!hideTabFirefly && portaled ? (
        <FireflyViewportPortal
          viewportX={portaledFireflyX}
          viewportY={portaledFireflyY}
          smoothTransition={!dragChaseActive}
        >
          <Firefly
            {...fireflyCommon}
            dragChasing={dragChaseActive}
            anchorX={0}
            anchorY={0}
            highlightGeneration={0}
            highlightX={highlightPoint.x}
            highlightY={highlightPoint.y}
            ambientHover={roaming || dragChaseActive}
            motionResetGeneration={motionResetGeneration}
          />
        </FireflyViewportPortal>
      ) : null}
    </>
  );
}
